import React, { MouseEventHandler, useCallback, useContext, useMemo } from 'react'
import moment from 'moment'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import LuxPlusLabelWithModal from 'luxPlus/components/LuxPlusLabelWithModal'
import GeoContext from 'contexts/geoContext'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import OfferPackagePartnershipLabel from 'components/Common/Labels/OfferPackagePartnershipLabel'
import TextLink from 'LuxKit/TextLink'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import InclusionsSection from 'components/OfferPage/FlashHotel/InclusionsSection'
import LineExclamationCircleIcon from 'LuxKit/Icons/line/LineExclamationCircleIcon'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import AccommodationPackageTile from 'components/AccommodationKit/AccommodationPackageTile'
import AccommodationPackageTileContentArea from 'components/AccommodationKit/AccommodationPackageTileContentArea'
import AccommodationPackageTilePricingArea from 'components/AccommodationKit/AccommodationPackageTilePricingArea'
import AccommodationPackageTileDetails from 'components/AccommodationKit/AccommodationPackageTileDetails'
import FlashHotelOptionPriceDetailsVariant from './FlashHotelOptionPriceDetailsVariant'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import AccommodationPackageTileHeaderArea from 'components/AccommodationKit/AccommodationPackageTileHeaderArea'
import { useAppSelector } from 'hooks/reduxHooks'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import OfferPageLoyaltyStayUpgradeCallOutBanner from 'components/OfferPage/Loyalty/OfferPageLoyaltyStayUpgradeCallOutBanner'
import useLoyaltyOfferPageTargetStayUpgrade from 'components/OfferPage/hooks/useLoyaltyOfferPageTargetStayUpgrade'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'
import useLoyaltyOfferPageClearTargetStayUpgrade from 'components/OfferPage/hooks/useLoyaltyOfferPageClearTargetStayUpgrade'
import styled from 'styled-components'
import { rem } from 'polished'
import cn from 'clsx'
import useLoyaltyTargetUpgradePackages from 'loyalty/hooks/useLoyaltyTargetUpgradePackages'
import { useActiveInclusionFilters } from 'hooks/useActiveInclusionFilters'
import { reorderTileInclusionsByActiveFilters } from 'lib/offer/inclusionUtils'

const InclusionsContainer = styled.div`
  display: grid;
  grid-template-areas: 'base-package' 'target-upgrade-package';
  grid-template-rows: 1fr;
`

const BasePackageInclusions = styled.div`
  grid-area: base-package;
  grid-row: 1;
  opacity: 1;
  transform: translateY(0);
  transition: opacity 300ms ease, transform 300ms ease;
  transition-delay: 300ms;
  
  &:not(.show) {
    opacity: 0;
    transform: translateY(${rem(16)});
    transition-delay: 0ms;
    pointer-events: none;
  }
`

const TargetUpgradePackageInclusions = styled.div`
  grid-area: target-upgrade-package;
  grid-row: 1;
  opacity: 1;
  transform: translateY(${rem(0)});
  transition: opacity 300ms ease, transform 300ms ease;
  transition-delay: 300ms;
  
  &:not(.show) {
    opacity: 0;
    transform: translateY(${rem(-16)});
    transition-delay: 0ms;
    pointer-events: none;
  }
`

interface Props {
  optionNumber: number
  offer: App.Offer
  packageView: App.OfferPackageOptionView
  roomsBeforeSurcharge?: number
  checkIn?: string
  duration: number
  flightDestination?: App.Airport
  previewMode: boolean
  isLuxPlusEnabled: boolean
  children?: React.ReactNode
  showOptionLabel?: boolean
  availableForPurchase?: boolean
}

function FlashHotelRateOptionVariant(props: Props) {
  const {
    offer,
    packageView,
    roomsBeforeSurcharge,
    checkIn,
    duration,
    optionNumber,
    flightDestination,
    previewMode,
    isLuxPlusEnabled,
    children,
    showOptionLabel = true,
    availableForPurchase,
  } = props

  const { partnership } = useContext(GeoContext)
  const {
    package: pkg,
  } = packageView

  const showPartnership =
    (packageView.hotelAvailable &&
      !!partnership?.hasEarn &&
      offer.canEarnPartnershipPoints)

  const showPrices = (packageView.capacityValid && packageView.hotelAvailable) || previewMode
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const checkOutMoment = useMemo(() => checkInMoment?.clone().add(duration, 'days'), [checkInMoment, duration])

  const { inclusions, bonusInclusions, luxPlusInclusions } = useOfferInclusions(pkg, { checkIn: checkInMoment, checkOut: checkOutMoment })

  const {
    selectedTargetUpgradePackage,
    selectedTargetUpgradeType,
  } = useLoyaltyOfferPageTargetStayUpgrade(pkg)

  const {
    inclusions: targetUpgradeInclusions,
    bonusInclusions: targetUpgradeBonusInclusions,
    luxPlusInclusions: targetUpgradeLuxPlusInclusions,
  } = useOfferInclusions(selectedTargetUpgradePackage, { checkIn: checkInMoment, checkOut: checkOutMoment })

  const targetUpgradePackages = useLoyaltyTargetUpgradePackages(pkg, offer)
  const loyaltyRoomUpgradePkg = targetUpgradePackages?.room
  const loyaltyPackageUpgradePkg = targetUpgradePackages?.package

  const {
    inclusions: loyaltyRoomUpgradeInclusions,
    bonusInclusions: loyaltyRoomUpgradeBonusInclusions,
    luxPlusInclusions: loyaltyRoomUpgradeLuxPlusInclusions,
  } = useOfferInclusions(loyaltyRoomUpgradePkg, { checkIn: checkInMoment, checkOut: checkOutMoment })

  const {
    inclusions: loyaltyPackageUpgradeInclusions,
    bonusInclusions: loyaltyPackageUpgradeBonusInclusions,
    luxPlusInclusions: loyaltyPackageUpgradeLuxPlusInclusions,
  } = useOfferInclusions(loyaltyPackageUpgradePkg, { checkIn: checkInMoment, checkOut: checkOutMoment })

  const activeInclusionCategories = useActiveInclusionFilters()

  // Boost regular package inclusions based on active filters (not bonus or loyalty inclusions)
  const boostedInclusions = useMemo(() => {
    if (!inclusions.length) return inclusions
    return reorderTileInclusionsByActiveFilters(inclusions, activeInclusionCategories)
  }, [inclusions, activeInclusionCategories])

  const hasInclusions = useMemo(() => {
    if (!selectedTargetUpgradePackage) {
      return luxPlusInclusions.length > 0 || boostedInclusions.length > 0 || bonusInclusions.length > 0
    }
    return targetUpgradeLuxPlusInclusions.length > 0 || targetUpgradeInclusions.length > 0 || targetUpgradeBonusInclusions.length > 0
  }, [selectedTargetUpgradePackage, luxPlusInclusions, boostedInclusions, bonusInclusions, targetUpgradeLuxPlusInclusions, targetUpgradeInclusions, targetUpgradeBonusInclusions])

  const memberPrice = packageView.baseMemberPrice ?? 0
  const showMemberPrice = isLuxPlusEnabled && memberPrice > 0
  const saveAmount = showMemberPrice ? (packageView.basePrice ?? 0) - memberPrice : undefined

  const offerPageDispatch = useOfferPageDispatch()

  const handleOpenCalendar = useCallback<MouseEventHandler<HTMLAnchorElement>>(() => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [offerPageDispatch, pkg.roomType])

  const openCapacityFilter = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [offerPageDispatch, pkg.roomType])

  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const isLoyaltyRoomUpgradesAvailable = useUserHasLoyaltyBenefit('hotel_upgrade')
  const showDynamicPricingUrgencyTag = !!roomsBeforeSurcharge
  const INCLUSIONS_COLLAPSED_MAX_HEIGHT = 80

  useLoyaltyOfferPageClearTargetStayUpgrade(packageView)

  return (
    <AccommodationPackageTile data-testid={pkg.uniqueKey}>
      {isLoyaltyEnabled && isLoyaltyRoomUpgradesAvailable && <AccommodationPackageTileHeaderArea>
        <OfferPageLoyaltyStayUpgradeCallOutBanner packageView={packageView} />
      </AccommodationPackageTileHeaderArea>}
      <AccommodationPackageTileContentArea>
        <AccommodationPackageTileDetails
          packageNumber={showOptionLabel ? optionNumber : undefined}
          roomPackage={packageView.package}
          selectedTargetUpgradePackage={selectedTargetUpgradePackage}
          targetUpgradePackage={targetUpgradePackages?.room ?? targetUpgradePackages?.package}
        >
          <Group direction="vertical" noDisplayWhenEmpty gap={4}>
            {availableForPurchase && offer.property && <>
              {!selectedTargetUpgradePackage && pkg.roomRate && <FlashCancellationPolicies
                checkInDate={checkIn}
                propertyTimezoneOffset={offer.property.timezoneOffset}
                cancellationPolicy={pkg.roomRate.cancellationPolicy}
              />}
              {selectedTargetUpgradePackage?.roomRate && <FlashCancellationPolicies
                checkInDate={checkIn}
                propertyTimezoneOffset={offer.property.timezoneOffset}
                cancellationPolicy={selectedTargetUpgradePackage.roomRate.cancellationPolicy}
              />}
            </>}
            {!!showPartnership && !hasInclusions && pkg.partnership && (
              <OfferPackagePartnershipLabel
                offer={offer}
                price={packageView.hotelPrice}
                packagePartnership={pkg.partnership}
              />
            )}
            <LabelGroup>
              {checkIn && !showDynamicPricingUrgencyTag && <RoomsRemainingLabel roomCount={packageView.availableRooms ?? 0} threshold="rate" />}
              {saveAmount && offer.luxPlus.access !== 'memberOnly' &&
                <CSSBreakpoint min="desktop">
                  <LuxPlusLabelWithModal
                    type="member-price-offer"
                    offer={offer}
                    saveAmount={saveAmount}
                    hideSaveAmountPlus
                    inclusions={luxPlusInclusions}
                  />
                </CSSBreakpoint>}
            </LabelGroup>

          </Group>
        </AccommodationPackageTileDetails>
        <div>
          <Group direction="vertical" noDisplayWhenEmpty gap={4}>
            <InclusionsContainer>
              <BasePackageInclusions className={cn({ 'show': !selectedTargetUpgradePackage })}>
                <LargeContentToggle
                  maxHeight={INCLUSIONS_COLLAPSED_MAX_HEIGHT}
                  showMoreText="View more inclusions"
                  showLessText="View less inclusions"
                >
                  <VerticalSpacer gap={8}>
                    {!!luxPlusInclusions.length && <LuxPlusInclusions
                      luxPlusInclusions={luxPlusInclusions}
                      accommodationOffer={offer}
                      hideUpsellModal
                    />}
                    <InclusionsSection inclusions={boostedInclusions} bonusInclusions={bonusInclusions} />
                  </VerticalSpacer>
                </LargeContentToggle>
              </BasePackageInclusions>
              {loyaltyRoomUpgradePkg && <TargetUpgradePackageInclusions className={cn({ 'show': selectedTargetUpgradeType === 'room' })}>
                <LargeContentToggle
                  maxHeight={INCLUSIONS_COLLAPSED_MAX_HEIGHT}
                  showMoreText="View more inclusions"
                  showLessText="View less inclusions"
                >
                  <VerticalSpacer gap={8}>
                    {!!loyaltyRoomUpgradeLuxPlusInclusions.length && <LuxPlusInclusions
                      luxPlusInclusions={loyaltyRoomUpgradeLuxPlusInclusions}
                      accommodationOffer={offer}
                      hideUpsellModal
                    />}
                    <InclusionsSection inclusions={loyaltyRoomUpgradeInclusions} bonusInclusions={loyaltyRoomUpgradeBonusInclusions} />
                  </VerticalSpacer>
                </LargeContentToggle>
              </TargetUpgradePackageInclusions>}
              {loyaltyPackageUpgradePkg && <TargetUpgradePackageInclusions className={cn({ 'show': selectedTargetUpgradeType === 'package' })}>
                <LargeContentToggle
                  maxHeight={INCLUSIONS_COLLAPSED_MAX_HEIGHT}
                  showMoreText="View more inclusions"
                  showLessText="View less inclusions"
                >
                  <VerticalSpacer gap={8}>
                    {!!loyaltyPackageUpgradeLuxPlusInclusions.length && <LuxPlusInclusions
                      luxPlusInclusions={loyaltyPackageUpgradeLuxPlusInclusions}
                      accommodationOffer={offer}
                      hideUpsellModal
                    />}
                    <InclusionsSection inclusions={loyaltyPackageUpgradeInclusions} bonusInclusions={loyaltyPackageUpgradeBonusInclusions} />
                  </VerticalSpacer>
                </LargeContentToggle>
              </TargetUpgradePackageInclusions>}
            </InclusionsContainer>
            {hasInclusions && !!showPartnership && pkg.partnership && (
              <VerticalSpacer gap={12}>
                <OfferPackagePartnershipLabel
                  offer={offer}
                  price={packageView.hotelPrice}
                  packagePartnership={pkg.partnership}
                />
              </VerticalSpacer>
            )}
          </Group>
        </div>
      </AccommodationPackageTileContentArea>
      <AccommodationPackageTilePricingArea>
        {children}
        {!children && showPrices && <FlashHotelOptionPriceDetailsVariant
          roomsBeforeSurcharge={roomsBeforeSurcharge}
          packageView={packageView}
          flightDestination={flightDestination}
        />}
        {!children && !packageView.capacityValid && (
          <MessageBanner
            kind="informational-primary"
            icon={<LineExclamationCircleIcon />}
            heading="This room cannot accommodate your number of guests"
          >
            Choose another room or <TextLink size="medium" weight="regular" onClick={openCapacityFilter}>add more rooms</TextLink>
          </MessageBanner>
        )}
        {!children && !packageView.hotelAvailable && packageView.capacityValid && (
          <MessageBanner
            kind="informational-primary"
            icon={<LineExclamationCircleIcon />}
            heading="This room is sold out on your dates or max number of guests exceeded"
          >
            Choose another room or <TextLink size="medium" weight="regular" onClick={handleOpenCalendar}>try different dates</TextLink>
          </MessageBanner>
        )}
      </AccommodationPackageTilePricingArea>
    </AccommodationPackageTile>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  checkIn: state.checkInDate,
  duration: state.duration,
  partnership: appState.geo.partnership,
  previewMode: appState.system.previewMode,
  isLuxPlusEnabled: getIsLuxPlusEnabled(appState),
  availableForPurchase: isAvailableForPurchase(state, appState),
}), FlashHotelRateOptionVariant)
